import { TypedDocumentNode, gql } from '@apollo/client';
import {
  faCaretLeft,
  faCaretRight,
  faCircleInfo,
} from '@fortawesome/pro-solid-svg-icons';
import { SetStateAction, Suspense, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useKey } from 'react-use';
import styled from 'styled-components';

import {
  ProbabilisticBundleDesign,
  Rarity,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { HeadlineL, LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { Alert } from '@sorare/core/src/atoms/ui/Alert';
import { Countdown } from '@sorare/core/src/components/Countdown';
import { No3dSupport } from '@sorare/core/src/components/No3dSupport';
import { ConfirmDialog } from '@sorare/core/src/components/form/ConfirmDialog';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { messages } from '@sorare/core/src/components/rewards/RewardBoxDialog/utils';
import { ANY_SPORT_PLAY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import usePaginatedQuery from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useTimeLeft } from '@sorare/core/src/hooks/useTimeLeft';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { lazy } from '@sorare/core/src/lib/retry';

import { BoxOverviewDialog } from 'components/rewards/BoxOverviewDialog';
import { useFormatBox } from 'components/rewards/RewardBoxDialog/utils';
import { RewardBoxFallback } from 'components/rewards/RewardBoxFallback';

import {
  InventoryBoxesTypeQuery,
  InventoryBoxesTypeQueryVariables,
} from './__generated__/index.graphql';

const Hoverable = lazy(async () => import('@sorare/trois/src/atoms/Hoverable'));

const MouseCamera = lazy(
  async () => import('@sorare/trois/src/atoms/MouseCamera')
);
const Steps = lazy(async () => import('@sorare/trois/src/atoms/Steps'));

const Box = lazy(
  async () => import('@sorare/trois/src/components/ClaimRewardBoxes/Box')
);

const Factory = lazy(
  async () => import('@sorare/trois/src/components/Factory')
);
const PAGE_SIZE = 50;
const MY_CARDS_BOXES_TYPE_QUERY = gql`
  query InventoryBoxesTypeQuery(
    $sport: Sport!
    $design: ProbabilisticBundleDesign
    $rarity: Rarity
    $after: String
    $first: Int!
  ) {
    currentUser {
      slug
      unopenedProbabilisticBundles(
        sport: $sport
        design: $design
        rarity: $rarity
        after: $after
        first: $first
      ) {
        nodes {
          id
          openingDisclaimers {
            slug
            message
          }
          openableAt
          isOpenable
          config {
            id
            design
            rarity
            division
            pictureUrl
            ...BoxOverviewDialog_probabilisticBundleConfig
          }
          so5Reward {
            slug
            so5Leaderboard {
              slug
              so5League {
                slug
                ...BoxOverviewDialog_so5League
              }
            }
          }
          ...useFormatBox_ProbabilisticBundle
          ...RewardBoxFallback_ProbabilisticBundle
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
  ${useFormatBox.fragments.ProbabilisticBundle}
  ${BoxOverviewDialog.fragments.probabilisticBundleConfig}
  ${BoxOverviewDialog.fragments.so5League}
  ${RewardBoxFallback.fragments.ProbabilisticBundle}
` as TypedDocumentNode<
  InventoryBoxesTypeQuery,
  InventoryBoxesTypeQueryVariables
>;

const PageHeaderPortal = styled.div`
  padding: var(--unit);
`;
const Root = styled(Vertical)`
  height: 100%;
`;
const Content = styled.div`
  height: 100%;
  min-height: 0;
  flex: 1;
  position: relative;
`;
const StyledIconButton = styled(IconButton)`
  position: absolute;
  right: var(--double-unit);
  top: var(--double-unit);
  z-index: 1;
`;
const Warning = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--double-unit);
  position: absolute;
  z-index: 1;
  inset: var(--unit) var(--unit) auto;
`;
const Alerts = styled(Vertical)`
  width: 100%;
`;

const CountdownWrapper = styled.div`
  position: absolute;
  top: 10%;
  width: min-content;
  left: 0;
  right: 0;
  margin: auto;
  background: var(--c-nl-600);
  border-radius: var(--double-unit);
  padding: var(--double-unit);
`;

type Props = {
  sport: Sport;
  type: string;
  withPageHeader?: boolean;
};

export const InventoryBoxesTypePage = ({
  sport,
  type,
  withPageHeader = true,
}: Props) => {
  const { formatMessage } = useIntl();
  const { generateSportContextPath } = useSportContext();
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const [oddsDialogOpen, setOddsDialogOpen] = useState(false);
  const [openWarning, setOpenWarning] = useState<() => void>();

  const { data, loadMore } = usePaginatedQuery(MY_CARDS_BOXES_TYPE_QUERY, {
    connections: ['ProbabilisticBundleConnection'],
    variables: {
      sport,
      first: PAGE_SIZE,
      rarity: {
        limited: Rarity.limited,
        rare: Rarity.rare,
        super_rare: Rarity.super_rare,
        unique: Rarity.unique,
      }[type],
      design: type === 'rivals' ? ProbabilisticBundleDesign.RIVALS : undefined,
    },
  });

  const { unopenedProbabilisticBundles } = data?.currentUser || {};
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [opening, setOpening] = useState<boolean>(false);
  const goToIndex = (value: SetStateAction<number>) => {
    setCurrentIndex(value);
    setOpening(false);
  };

  const { hasNextPage, endCursor } =
    data?.currentUser?.unopenedProbabilisticBundles.pageInfo ?? {};
  const lastFetchedCursor = useRef<string | undefined>(undefined);
  if (
    hasNextPage &&
    endCursor &&
    endCursor !== lastFetchedCursor.current &&
    (data?.currentUser?.unopenedProbabilisticBundles.nodes.length ?? 0) -
      currentIndex <
      PAGE_SIZE / 10
  ) {
    lastFetchedCursor.current = endCursor;

    loadMore(false, { after: endCursor });
  }

  const filteredBoxes =
    unopenedProbabilisticBundles?.nodes?.filter(box =>
      type && type in Rarity
        ? box.config.rarity === type &&
          box.config.design !== ProbabilisticBundleDesign.RIVALS
        : box.config.design === ProbabilisticBundleDesign.RIVALS
    ) || [];

  const boxes = useFormatBox(filteredBoxes);

  useEffect(() => {
    goToIndex(0);
  }, [type]);
  const prev = () => goToIndex(i => Math.max(0, i - 1));
  const next = () => goToIndex(i => Math.min(boxes.length - 1, i + 1));
  useKey('ArrowLeft', prev, undefined, [prev]);
  useKey('ArrowRight', next, undefined, [next]);

  const selectedBox = boxes.at(currentIndex);
  const rawBox = filteredBoxes.at(currentIndex);
  const selectedBoxNotOpened = boxes.find(b => b.id === selectedBox?.id);
  const disclaimers = filteredBoxes
    .at(currentIndex)
    ?.openingDisclaimers.filter(disclaimer => !!disclaimer.message) as {
    message: string;
    slug: string;
  }[];

  const { isEnded } = useTimeLeft(selectedBox?.openableAt);
  const boxIsLocked =
    selectedBox &&
    (selectedBox.openableAt ? !isEnded : !selectedBox.isOpenable);

  return (
    <>
      {withPageHeader && (
        <PageHeader
          backTo={generateSportContextPath(ANY_SPORT_PLAY)}
          text={isDesktopAndAbove ? glossary.inventory : undefined}
          customTitle={
            !isDesktopAndAbove ? (
              <SBHorizontal>
                <Vertical gap={0}>
                  <HeadlineL as="p">
                    <FormattedMessage {...glossary.inventory} />
                  </HeadlineL>
                  <LabelM as="p">
                    <FormattedMessage {...fantasy.boxes} />
                  </LabelM>
                </Vertical>
                {selectedBox && (
                  <StyledIconButton
                    color="transparent"
                    icon={faCircleInfo}
                    onClick={() => setOddsDialogOpen(true)}
                  />
                )}
              </SBHorizontal>
            ) : undefined
          }
        />
      )}
      <ConditionalWrapper
        Wrapper={FullWidthContainer.NoPadding}
        wrap={isDesktopAndAbove}
      >
        <Root>
          {withPageHeader && !isDesktopAndAbove && (
            <PageHeaderPortal id="page-header-title" />
          )}
          <Content>
            <Warning>
              {!!disclaimers?.length && (
                <Alerts>
                  {disclaimers.map(({ slug, message }) => (
                    <Alert
                      key={slug}
                      title={formatMessage({
                        id: 'InventoryBoxesTypePage.Warning.title',
                        defaultMessage: 'Warning',
                      })}
                      message={message!}
                    />
                  ))}
                </Alerts>
              )}
              {selectedBox && isDesktopAndAbove && (
                <IconButton
                  color="transparent"
                  icon={faCircleInfo}
                  onClick={() => setOddsDialogOpen(true)}
                />
              )}
            </Warning>

            <Suspense fallback={<LoadingIndicator fullHeight />}>
              <>
                <Factory
                  sport={sport}
                  context="box"
                  fallback={
                    <No3dSupport>
                      <RewardBoxFallback rewardBoxes={filteredBoxes} />
                    </No3dSupport>
                  }
                >
                  <Steps
                    distance={20}
                    steps={boxes.map(box => (
                      <Hoverable
                        key={box.id}
                        onClick={
                          box.isOpenable && !disclaimers?.length
                            ? () => {
                                setOpening(true);
                              }
                            : undefined
                        }
                      >
                        <Box
                          box={box}
                          turnAround={false}
                          onClick={
                            disclaimers?.length
                              ? callback => {
                                  setOpenWarning(() => () => {
                                    setOpening(true);
                                    callback();
                                  });
                                }
                              : undefined
                          }
                        />
                      </Hoverable>
                    ))}
                    currentIndex={currentIndex}
                  />
                  <MouseCamera disabled={opening} />
                </Factory>
                <IconButton
                  color="transparent"
                  onClick={prev}
                  disableDebounce
                  aria-label={formatMessage(glossary.previous)}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: 'var(--double-unit)',
                    transform: 'translate(-50%, -50%)',
                    display: currentIndex !== 0 ? 'block' : 'none',
                  }}
                >
                  <FontAwesomeIcon icon={faCaretLeft} fontSize={64} />
                </IconButton>
                <IconButton
                  color="transparent"
                  onClick={next}
                  disableDebounce
                  aria-label={formatMessage(glossary.next)}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: 'var(--double-unit)',
                    transform: 'translate(-50%, -50%)',
                    display: currentIndex < boxes.length - 1 ? 'block' : 'none',
                  }}
                >
                  <FontAwesomeIcon icon={faCaretRight} fontSize={64} />
                </IconButton>
                {selectedBox && boxIsLocked && selectedBox.openableAt && (
                  <CountdownWrapper>
                    <Countdown date={selectedBox.openableAt} />
                  </CountdownWrapper>
                )}
                {selectedBox && selectedBoxNotOpened && (
                  <LabelL
                    as="span"
                    color="var(--c-white)"
                    bold
                    style={{
                      position: 'absolute',
                      bottom: 'var(--quadruple-unit)',
                      left: 0,
                      right: 0,
                      textAlign: 'center',
                      display: boxes.length ? 'initial' : 'none',
                    }}
                  >
                    {boxIsLocked ? (
                      <FormattedMessage
                        id="RewardBox.locked"
                        defaultMessage="This Reward Box is currently locked"
                      />
                    ) : (
                      <FormattedMessage {...messages.clickToOpenIt} />
                    )}
                  </LabelL>
                )}
              </>
            </Suspense>
          </Content>
        </Root>
        {oddsDialogOpen && rawBox && (
          <BoxOverviewDialog
            open
            onClose={() => setOddsDialogOpen(false)}
            boxConfig={rawBox.config}
            so5League={rawBox.so5Reward?.so5Leaderboard.so5League}
          />
        )}
        {openWarning && !!disclaimers?.length && (
          <ConfirmDialog
            title={
              <HeadlineL>
                <FormattedMessage
                  id="InventoryBoxesTypePage.ConfirmDialog.title"
                  defaultMessage="Are you sure you want to open this box?"
                />
              </HeadlineL>
            }
            message={disclaimers[0].message}
            onConfirm={openWarning}
            ctaProps={{ color: 'red' }}
            cta={<FormattedMessage {...glossary.continue} />}
            onClose={() => setOpenWarning(undefined)}
            open
          />
        )}
      </ConditionalWrapper>
    </>
  );
};

export default InventoryBoxesTypePage;
